'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { type FC, useMemo } from 'react';
import { useRetentionStrategy } from '@/app/components/shared/RetentionStrategy';
import { SITE } from '@/app/constants';
import { useUserNexus } from '@/app/hooks';
import { cn } from '@/app/utils';

const pad2 = (value: number) => String(value).padStart(2, '0');

const AnimatedDigit: FC<{ digit: string }> = ({ digit }) => (
  <span className="relative inline-flex h-[1em] w-[0.6em] overflow-hidden">
    <AnimatePresence mode="popLayout" initial={false}>
      <motion.span
        key={digit}
        initial={{ y: '-100%', opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: '100%', opacity: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="absolute inset-0 flex items-center justify-center"
      >
        {digit}
      </motion.span>
    </AnimatePresence>
  </span>
);

const TimeBlock = ({ value, label }: { value: string; label: string }) => (
  <div className="flex shrink-0 flex-col items-center justify-center rounded-16 bg-[rgba(0,0,0,0.24)] p-3 backdrop-blur-[10px]">
    <div className="flex items-center gap-0.5 font-bold text-[36px] text-white tabular-nums leading-none">
      <AnimatedDigit digit={value[0]} />
      <AnimatedDigit digit={value[1]} />
    </div>
    <div className="text-white/40 text-xs">{label}</div>
  </div>
);

export const DiscountBanner: FC<{ className?: string }> = ({ className }) => {
  const { isSubOneTimeOffer } = useUserNexus();
  const {
    countdownHours,
    countdownMinutes,
    countdownSeconds,
    phase: retentionPhase,
    daysToExpiry,
  } = useRetentionStrategy();
  const t = useTranslations();

  const is7DaysLeft = retentionPhase === 'phase2';
  const isLastDay = retentionPhase === 'phase3';

  const isDiscountActivity = isSubOneTimeOffer || isLastDay || is7DaysLeft;

  const discountActivity = useMemo(() => {
    if (isSubOneTimeOffer) {
      return {
        title: t('payments.firstDiscountDialog.title'),
        description: t('payments.firstDiscountDialog.description'),
        banner:
          SITE === 'china'
            ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/first_discount-28af25eb.png'
            : 'https://image01.cf.vidu.studio/vidu/media-asset/first_discount-28af25eb.png',
      };
    }
    if (is7DaysLeft) {
      return {
        title: t('payments.mySubscription.subscriptionExpired', { daysToExpiry }),
        banner:
          SITE === 'china'
            ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/retention_7days_left-af8fee1d.png'
            : 'https://image01.cf.vidu.studio/vidu/media-asset/retention_7days_left-af8fee1d.png',
      };
    }
    if (isLastDay) {
      return {
        title: t('payments.mySubscription.todayExpired'),
        banner:
          SITE === 'china'
            ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/retention_1day_left-f29b3bdb.png'
            : 'https://image01.cf.vidu.studio/vidu/media-asset/retention_1day_left-f29b3bdb.png',
      };
    }
    return null;
  }, [daysToExpiry, is7DaysLeft, isLastDay, isSubOneTimeOffer]);

  if (!isDiscountActivity) return null;

  return (
    <div
      className={cn(
        'relative mt-3 flex h-[120px] items-center justify-between overflow-hidden rounded-16 bg-center bg-cover bg-no-repeat pr-5 pl-10 sm:mx-4 sm:h-[100px] sm:w-[calc(100%-32px)] sm:px-4',
        className,
      )}
      style={{ backgroundImage: `url(${discountActivity?.banner})` }}
    >
      <div className="flex flex-col gap-1">
        <div className="font-medium text-white text-xl">{discountActivity?.title}</div>
        {discountActivity?.description && (
          <div className="text-sm text-system-white48">{discountActivity?.description}</div>
        )}
      </div>

      {isLastDay && (
        <div className="flex items-center gap-3 p-2">
          <TimeBlock value={pad2(countdownHours)} label={t('payments.mySubscription.discountBannerCountdownHours')} />
          <div className="text-[40px] text-system-white40">:</div>
          <TimeBlock
            value={pad2(countdownMinutes)}
            label={t('payments.mySubscription.discountBannerCountdownMinutes')}
          />
          <div className="text-[40px] text-system-white40">:</div>
          <TimeBlock
            value={pad2(countdownSeconds)}
            label={t('payments.mySubscription.discountBannerCountdownSeconds')}
          />
        </div>
      )}
    </div>
  );
};
